import { useEffect, useMemo, useState } from "react";
import { Admin } from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";

type Row = {
  id: string;
  action: string;
  target: string | null;
  actorId: string | null;
  actorUsername: string | null;
  createdAt: string; // ISO
  payload?: Record<string, unknown> | null;
};

function isoLocal(date?: Date) {
  const d = date ?? new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function AdminAuditPage() {
  const { user } = useAuth();
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");   // id veya username
  const [target, setTarget] = useState(""); // prefix: post:, user:, comment:
  const [from, setFrom] = useState<string>(isoLocal(new Date(Date.now() - 7 * 86400000))); // son 7 gün
  const [to, setTo] = useState<string>(isoLocal());
  const [before, setBefore] = useState<string>("");

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);

  const canUse = !!user?.isAdmin;

  const query = useMemo(() => {
    const q: any = {};
    if (action.trim()) q.action = action.trim();
    if (actor.trim()) q.actor = actor.trim();
    if (target.trim()) q.target = target.trim();
    if (from) q.from = new Date(from).toISOString();
    if (to) q.to = new Date(to).toISOString();
    if (before.trim()) q.before = new Date(before).toISOString();
    return q;
  }, [action, actor, target, from, to, before]);

  async function load(reset = true) {
    setLoading(true);
    try {
      const params = { ...query } as any;
      // server cursor alanı "before" ile çalışıyor. UI'da load more için rows'un son createdAt'ini gönderiyoruz.
      if (!reset && rows.length) {
        params.before = rows[rows.length - 1].createdAt;
      }
      const res = await Admin.listAudit(params);
      setRows(reset ? res.rows : [...rows, ...res.rows]);
      setCursor(res.rows.length ? res.rows[res.rows.length - 1].createdAt : null);
    } catch (e) {
      console.error(e);
      alert("Audit listesi alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canUse) return;
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse, action, actor, target, from, to, before]);

  if (!canUse) {
    return (
      <div style={{ padding: 16 }}>
        <h2>Audit</h2>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 12 }}>Admin · Audit</h2>

      {/* Filtreler */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 220px 220px 220px",
          gap: 8,
          marginBottom: 12,
          alignItems: "center",
        }}
      >
        <input
          placeholder="action (örn: post.delete)"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          style={inputStyle}
        />
        <input
          placeholder="actor (ObjectId veya username)"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          style={inputStyle}
        />
        <input
          placeholder="target prefix (örn: post:, user:, comment:)"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          style={inputStyle}
        />

        <div style={{ display: "flex", gap: 6 }}>
          <label style={labelStyle}>From</label>
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: "flex", gap: 6 }}>
          <label style={labelStyle}>To</label>
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: "flex", gap: 6 }}>
          <label style={labelStyle}>Before</label>
          <input
            type="datetime-local"
            value={before}
            onChange={(e) => setBefore(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ textAlign: "right", marginBottom: 12 }}>
        <button disabled={loading} onClick={() => load(true)}>Uygula / Yenile</button>
      </div>

      {/* Tablo */}
      <div style={{ width: "100%", overflowX: "auto", border: "1px solid var(--muted)", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#0f0f0f" }}>
              <th style={th}>Zaman</th>
              <th style={th}>Action</th>
              <th style={th}>Target</th>
              <th style={th}>Actor</th>
              <th style={th}>Payload</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <AuditRow key={r.id} row={r} />
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={5} style={{ padding: 16, textAlign: "center", opacity: 0.7 }}>
                  Sonuç yok
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button disabled={loading || !cursor} onClick={() => load(false)}>
          Daha eskileri yükle
        </button>
      </div>
    </div>
  );
}

function AuditRow({ row }: { row: Row }) {
  const [open, setOpen] = useState(false);
  const created = new Date(row.createdAt);
  const ts = `${created.toLocaleDateString()} ${created.toLocaleTimeString()}`;

  return (
    <tr>
      <td style={td}>
        <div title={row.createdAt}>{ts}</div>
      </td>
      <td style={td}>
        <code>{row.action}</code>
      </td>
      <td style={td}>
        {row.target ? <code>{row.target}</code> : <span style={{ opacity: 0.7 }}>—</span>}
      </td>
      <td style={td}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span title="actorId">{row.actorId ?? "—"}</span>
          <span style={{ opacity: 0.8 }} title="actorUsername">
            {row.actorUsername ?? "—"}
          </span>
        </div>
      </td>
      <td style={td}>
        {row.payload ? (
          <>
            <button onClick={() => setOpen((v) => !v)} style={{ marginBottom: 6 }}>
              {open ? "Gizle" : "Göster"}
            </button>
            {open && (
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxWidth: 420,
                  maxHeight: 200,
                  overflow: "auto",
                  margin: 0,
                  background: "#0b0b0b",
                  border: "1px solid #1a1a1a",
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                {JSON.stringify(row.payload, null, 2)}
              </pre>
            )}
          </>
        ) : (
          <span style={{ opacity: 0.7 }}>—</span>
        )}
      </td>
    </tr>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid var(--muted)",
  whiteSpace: "nowrap",
};

const td: React.CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid #1b1b1b",
  verticalAlign: "top",
};

const inputStyle: React.CSSProperties = {
  padding: 8,
  borderRadius: 8,
  border: "1px solid var(--muted)",
  background: "var(--bg2)",
  color: "var(--text)",
  width: "100%",
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.8,
  lineHeight: "30px",
};
